import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Input, Select, Button, Card, Tag, Icon } from "antd";
import { Editor } from "@tinymce/tinymce-react";
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";

import { sendSystemMessage } from "./systemMessages.ducks";
import { SystemMessageUserGroup, SystemMessageUserGroupText, SystemMessageType, SystemMessageTypeText } from "../../../models/enums";
import { notification } from "../../../services";
import { selectCategories } from "../../../containers/App/selectors";
import { Image } from "../../../services/api";
import { getImageLink } from "../../../services/asset";
import { photoEditAction } from "../../../models";
import styles from "./manageSystemMessagesPage.scss";

var setPendingFileGlobal = null;
var setCropperVisibleGlobal = null;

const SendSystemMessageModal = ({ visible, onCancel, onSuccess, initialData }) => {
  const dispatch = useDispatch();
  const categories = useSelector(selectCategories);
  var cropperRef = useRef(null);
  // Kept on ref so the cropper apply handler can call editor.insertContent(...)
  // without waiting for state updates. Set on TinyMCE init.
  var editorRef = useRef(null);
  var [cropperVisible, setCropperVisible] = useState(false);
  var [pendingFile, setPendingFile] = useState(null);
  var [uploadingCrop, setUploadingCrop] = useState(false);

  setPendingFileGlobal = setPendingFile;
  setCropperVisibleGlobal = setCropperVisible;

  const tinymceInit = {
    height: 460,
    menubar: false,
    plugins: [
      "advlist", "autolink", "lists", "link", "image", "table", "code", "help",
      "wordcount", "media"
    ],
    // Custom "image" button skips the built-in Image dialog and jumps straight
    // into our file picker → cropper → upload → insert flow.
    toolbar:
      "undo redo | formatselect fontsize | bold italic underline strikethrough | " +
      "forecolor backcolor | alignleft aligncenter alignright | " +
      "bullist numlist outdent indent | link customimage media table | removeformat | code",
    // Wrap onto multiple rows instead of hiding overflow behind the "…" button.
    toolbar_mode: "wrap",
    language: "hr",
    language_url:
      "https://cdn.jsdelivr.net/npm/tinymce-i18n@25.1.28/langs6/hr.js",
    content_style:
      "body { font-family:Arial,Helvetica,sans-serif; font-size:14px; } " +
      "table { border-collapse: collapse; width: 100%; } " +
      "table td, table th { border: 1px solid #d9d9d9; padding: 6px 8px; } " +
      "img { max-width: 100%; height: auto; }",
    branding: false,
    promotion: false,
    setup: function (editor: any) {
      editorRef.current = editor;
      editor.ui.registry.addButton("customimage", {
        icon: "image",
        tooltip: "Umetni sliku",
        onAction: function () {
          var input = document.createElement("input");
          input.setAttribute("type", "file");
          input.setAttribute("accept", "image/*");
          input.onchange = function () {
            var file = input.files && input.files[0];
            if (!file) return;
            if (setPendingFileGlobal) setPendingFileGlobal(file);
            if (setCropperVisibleGlobal) setCropperVisibleGlobal(true);
          };
          input.click();
        }
      });
    }
  };

  var handleCropperRendered = function (ref) {
    if (!ref || !pendingFile) return;

    var reader = new FileReader();
    reader.onload = function (e) {
      ref.onload = function () {
        cropperRef.current = new Cropper(ref, {
          background: false,
          autoCrop: false,
          checkOrientation: false,
        });
      };
      ref.src = e.target.result;
    };
    reader.readAsDataURL(pendingFile);
  };

  var handleEditAction = function (action) {
    var c = cropperRef.current;
    if (!c) return;

    switch (action) {
      case photoEditAction.rotateLeft:
        c.rotate(-90);
        break;
      case photoEditAction.rotateRight:
        c.rotate(90);
        break;
      case photoEditAction.flipHorizontal:
        c.scaleX(-c.getData().scaleX || -1);
        break;
      case photoEditAction.flipVertical:
        c.scaleY(-c.getData().scaleY || -1);
        break;
      case photoEditAction.reset:
        c.rotateTo(0);
        c.scaleX(1);
        c.scaleY(1);
        break;
    }
  };

  var handleCropApply = function () {
    var c = cropperRef.current;
    if (!c) return;

    var canvas = c.getCroppedCanvas();
    if (!canvas) return;

    var fileType = pendingFile ? pendingFile.type : "image/png";
    var fileName = pendingFile ? pendingFile.name : "image.png";

    setUploadingCrop(true);
    canvas.toBlob(function (blob) {
      var editedFile = new File([blob], fileName, { type: blob.type });

      Image.upload([editedFile]).then(function (data) {
        var url = getImageLink(data[0]);
        var editor = editorRef.current;
        if (editor) {
          // Insert directly at the current selection; TinyMCE's default image
          // resize handles kick in automatically once the <img> is in the DOM.
          editor.insertContent(
            '<img src="' + url + '" alt="' + fileName.replace(/"/g, "&quot;") + '" />'
          );
          editor.focus();
        }
        setCropperVisible(false);
        setPendingFile(null);
        cropperRef.current = null;
        setUploadingCrop(false);
      }).catch(function () {
        notification.showError("Greška pri uploadu slike");
        setUploadingCrop(false);
      });
    }, fileType);
  };

  var handleCropCancel = function () {
    setCropperVisible(false);
    setPendingFile(null);
    cropperRef.current = null;
  };

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [userGroup, setUserGroup] = useState(null);
  const [messageType, setMessageType] = useState(SystemMessageType.Normal);
  const [usernames, setUsernames] = useState([]);
  const [usernameInput, setUsernameInput] = useState("");
  const [category, setCategory] = useState(null);
  const [yesLabel, setYesLabel] = useState("Da");
  const [noLabel, setNoLabel] = useState("Ne");
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (initialData && visible) {
      setTitle(initialData.title || "");
      setContent(initialData.content || "");
      if (initialData.type !== undefined) setMessageType(initialData.type);
      if (initialData.userGroup !== undefined && initialData.userGroup !== null) setUserGroup(initialData.userGroup);
      if (initialData.yesPromptChoiceLabel) setYesLabel(initialData.yesPromptChoiceLabel);
      if (initialData.noPromptChoiceLabel) setNoLabel(initialData.noPromptChoiceLabel);
    }
  }, [initialData, visible]);

  const resetForm = () => {
    setTitle("");
    setContent("");
    setUserGroup(null);
    setUsernames([]);
    setUsernameInput("");
    setCategory(null);
    setMessageType(SystemMessageType.Normal);
    setYesLabel("Da");
    setNoLabel("Ne");
    setShowConfirm(false);
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  const isByUsername = userGroup === SystemMessageUserGroup.ByUsername;
  const isAdInCategory = userGroup === SystemMessageUserGroup.AdInCategory;

  const handleAddUsername = () => {
    const trimmed = usernameInput.trim();
    if (trimmed && !usernames.includes(trimmed)) {
      setUsernames([...usernames, trimmed]);
    }
    setUsernameInput("");
  };

  const handleRemoveUsername = (username) => {
    setUsernames(usernames.filter((u) => u !== username));
  };

  const handleUsernameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddUsername();
    }
  };

  const hasContent = content.replace(/<[^>]*>/g, "").trim().length > 0 || content.indexOf("<img") !== -1;
  const isValid = hasContent && userGroup !== null && (!isByUsername || usernames.length > 0) && (!isAdInCategory || category !== null);

  const handleSendClick = () => {
    if (!isValid) return;
    setShowConfirm(true);
  };

  const handleConfirmSend = () => {
    if (!isValid) return;

    const payload: any = {
      title: title.trim(),
      content: content.trim(),
      userGroup: userGroup,
      type: messageType,
    };

    if (messageType === SystemMessageType.PromptChoice) {
      payload.yesPromptChoiceLabel = yesLabel.trim() || "Da";
      payload.noPromptChoiceLabel = noLabel.trim() || "Ne";
    }

    if (isByUsername) {
      payload.usernames = usernames;
    }

    if (isAdInCategory) {
      payload.category = category;
    }

    setSending(true);
    dispatch(sendSystemMessage(payload, (success) => {
      setSending(false);
      if (success) {
        notification.show("Poruka je poslana! Dostava može potrajati do minute.");
        resetForm();
        onSuccess();
      } else {
        notification.showError("Greška pri slanju poruke");
      }
    }));
  };

  return (
    <Modal
      title="Nova sistemska poruka"
      visible={visible}
      onCancel={handleCancel}
      width={1100}
      destroyOnClose
      footer={
        <div className={styles.modalFooter}>
          <Button onClick={handleCancel}>Odustani</Button>
          <Button
            type="primary"
            onClick={handleSendClick}
            disabled={!isValid}
          >
            Pošalji
          </Button>
        </div>
      }
    >
      <div className={styles.modalColumns}>
        <div className={styles.modalColumnLeft}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Ciljna grupa *</label>
            <Select
              placeholder="Odaberi ciljnu grupu"
              value={userGroup}
              onChange={(val) => setUserGroup(val)}
              className={styles.fullWidth}
              getPopupContainer={(trigger) => trigger.parentElement}
            >
              {SystemMessageUserGroupText.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          {isByUsername && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Korisnička imena *</label>
              <div className={styles.usernamesInputRow}>
                <Input
                  placeholder="Unesi korisničko ime"
                  value={usernameInput}
                  onChange={(e) => setUsernameInput(e.target.value)}
                  onKeyDown={handleUsernameKeyDown}
                />
                <Button onClick={handleAddUsername} disabled={!usernameInput.trim()}>
                  Dodaj
                </Button>
              </div>
              {usernames.length > 0 && (
                <div className={styles.usernamesTags}>
                  {usernames.map((u) => (
                    <Tag key={u} closable onClose={() => handleRemoveUsername(u)}>
                      {u}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          )}

          {isAdInCategory && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Kategorija *</label>
              <Select
                placeholder="Odaberi kategoriju"
                value={category}
                onChange={(val) => setCategory(val)}
                className={styles.fullWidth}
                getPopupContainer={(trigger) => trigger.parentElement}
                showSearch
                optionFilterProp="children"
              >
                {categories.map((cat) => (
                  <Select.Option key={cat.name} value={cat.name}>
                    {cat.displayName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>Tip poruke *</label>
            <Select
              value={messageType}
              onChange={(val) => setMessageType(val)}
              className={styles.fullWidth}
              getPopupContainer={(trigger) => trigger.parentElement}
            >
              {SystemMessageTypeText.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          {messageType === SystemMessageType.PromptChoice && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Labele botuna</label>
              <div className={styles.promptLabelsRow}>
                <Input
                  placeholder="Da"
                  value={yesLabel}
                  onChange={(e) => setYesLabel(e.target.value)}
                  maxLength={50}
                />
                <Input
                  placeholder="Ne"
                  value={noLabel}
                  onChange={(e) => setNoLabel(e.target.value)}
                  maxLength={50}
                />
              </div>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>Naslov</label>
            <Input
              placeholder="Naslov poruke (opciono)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Sadržaj *</label>
            <div className={styles.editorWrapper}>
              <Editor
                tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@5.10.9/tinymce.min.js"
                value={content}
                onEditorChange={setContent}
                init={tinymceInit}
              />
            </div>
          </div>

        </div>

        <div className={styles.modalColumnRight}>
          <div className={styles.previewLabel}>Preview</div>
          <Card className={styles.previewCard} bordered={false}>
            {hasContent ? (
              <React.Fragment>
                {title && <h3>{title}</h3>}
                <div
                  className={styles.previewContent}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
                {messageType === SystemMessageType.PromptChoice && (
                  <div className={styles.previewChoiceButtons}>
                    <Button disabled>{noLabel || "Ne"}</Button>
                    <Button disabled>{yesLabel || "Da"}</Button>
                  </div>
                )}
              </React.Fragment>
            ) : (
              <p className={styles.previewPlaceholder}>
                Preview poruke će se prikazati ovdje...
              </p>
            )}
          </Card>
        </div>
      </div>

      <Modal
        title="Potvrda slanja"
        visible={showConfirm}
        onCancel={() => setShowConfirm(false)}
        onOk={handleConfirmSend}
        confirmLoading={sending}
        okText="Da, pošalji"
        cancelText="Odustani"
      >
        <p>
          Jeste li sigurni da želite poslati ovu poruku grupi{" "}
          <strong>{userGroup !== null ? (SystemMessageUserGroupText.find(o => o.value === userGroup) || {}).name : ""}</strong>?
        </p>
        <p className={styles.confirmNote}>
          Ova akcija se ne može poništiti. Poruka će biti dostavljena svim korisnicima u odabranoj grupi.
        </p>
      </Modal>

      <Modal
        width="90%"
        className={styles.cropperModal}
        visible={cropperVisible}
        onCancel={handleCropCancel}
        okText="Primjeni"
        onOk={handleCropApply}
        confirmLoading={uploadingCrop}
        destroyOnClose
      >
        <div className={styles.cropperContent}>
          <img
            className={styles.cropperEditor}
            alt="Edit"
            ref={handleCropperRendered}
          />
          <div className={styles.cropperControls}>
            <Icon
              className={styles.rotateLeft}
              type="reload"
              title="Zakreni lijevo"
              onClick={function () { handleEditAction(photoEditAction.rotateLeft); }}
            />
            <Icon
              type="reload"
              title="Zakreni desno"
              onClick={function () { handleEditAction(photoEditAction.rotateRight); }}
            />
            <Icon
              type="swap"
              title="Okreni vodoravno"
              onClick={function () { handleEditAction(photoEditAction.flipHorizontal); }}
            />
            <Icon
              className={styles.flipVertical}
              type="swap"
              title="Okreni okomito"
              onClick={function () { handleEditAction(photoEditAction.flipVertical); }}
            />
            <Icon
              type="sync"
              title="Resetiraj"
              onClick={function () { handleEditAction(photoEditAction.reset); }}
            />
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default SendSystemMessageModal;



// WEBPACK FOOTER //
// ./src/routes/Admin/ManageSystemMessagesPage/SendSystemMessageModal.tsx